<template>
  <div>
    <v-progress-linear v-if="cargando" indeterminate></v-progress-linear>
    <v-alert v-if="mensaje" dense outlined type="info">{{ mensaje }}</v-alert>

    <div v-if="datos">
      <div class="d-flex align-center mb-3">
        <v-chip v-if="datos.corte" small color="green" text-color="white">
          Aprobado, revisión {{ datos.corte.REVISION }}: {{ datos.corte.ING_USUARIO_ID }}, {{ datos.corte.ING_FECHA }}
        </v-chip>
        <v-chip v-else small>Sin corte</v-chip>
        <v-spacer></v-spacer>
        <template v-if="!datos.corte">
          <v-btn small class="mr-2" @click="abrirAjuste()">
            <v-icon left>mdi-plus</v-icon> Ajuste
          </v-btn>
          <v-btn small color="primary" :disabled="datos.errores.length > 0" @click="dialogAprobar = true">
            <v-icon left>mdi-check</v-icon> Aprobar corte
          </v-btn>
        </template>
        <v-btn v-else small color="error" @click="abrirAnular()">
          <v-icon left>mdi-close</v-icon> Anular corte
        </v-btn>
      </div>

      <v-alert v-if="datos.errores.length" dense outlined type="error">
        Corrija las líneas sin tarifa antes de aprobar.
        <ul>
          <li v-for="(error, i) in datos.errores" :key="i">
            {{ error.CONCEPTO }} (producto {{ error.PRO_ID }}): {{ error.msg }}
          </li>
        </ul>
      </v-alert>

      <v-alert v-if="datos.corte && datos.diferencias.length" dense outlined type="warning">
        La guía o las tarifas cambiaron después del corte. El corte no cambia; para usar los valores actuales, anúlelo y apruebe de nuevo.
        <v-simple-table dense class="mt-2 transparent">
          <thead>
            <tr>
              <th>Destino</th>
              <th>Dato</th>
              <th class="text-right">Corte</th>
              <th class="text-right">Actual</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(d, i) in datos.diferencias" :key="i">
              <td>{{ d.SCLIENTE_NOMBRE }}</td>
              <td>{{ d.campo }}</td>
              <td class="text-right">{{ monto(d.corte) }}</td>
              <td class="text-right">{{ monto(d.actual) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-alert>

      <v-alert v-if="datos.avisos.length" dense outlined type="warning">
        <ul>
          <li v-for="(aviso, i) in datos.avisos" :key="i">{{ aviso.msg }}</li>
        </ul>
      </v-alert>

      <v-card v-for="s in subclientes" :key="s.SCLIENTE_ID" outlined class="mb-4">
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Destino</th>
              <th class="text-right">HAWB</th>
              <th class="text-right">Extended Price</th>
              <th class="text-right">Duty</th>
              <th class="text-right">MPF</th>
              <th class="text-right">Total duties</th>
              <th class="text-right">Cajas</th>
              <th class="text-right">Peso</th>
            </tr>
          </thead>
          <tbody>
            <tr class="font-weight-bold">
              <td>{{ s.SCLIENTE_NOMBRE }} ({{ s.PAIS || "sin país" }})</td>
              <td class="text-right">{{ s.TOTAL_HAWB }}</td>
              <td class="text-right">{{ monto(s.EXTENDED_PRICE) }}</td>
              <td class="text-right">{{ monto(s.DUTY) }}</td>
              <td class="text-right">{{ monto(s.MPF) }}</td>
              <td class="text-right">{{ monto(s.TOTAL) }}</td>
              <td class="text-right">{{ monto(s.CAJAS) }}</td>
              <td class="text-right">{{ monto(s.PESO) }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-simple-table dense class="ml-6">
          <thead>
            <tr>
              <th>Descripción</th>
              <th class="text-right">Cantidad</th>
              <th class="text-right">Valor unitario</th>
              <th class="text-right">Valor total</th>
              <th v-if="!datos.corte" style="width: 40px"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in s.ITEMS" :key="i">
              <td>
                {{ item.DESCRIPCION }}
                <div v-if="item.MOTIVO" class="caption grey--text">Motivo: {{ item.MOTIVO }}</div>
              </td>
              <td class="text-right">{{ monto(item.CANTIDAD) }}</td>
              <td class="text-right">{{ monto(item.VALOR_UNI) }}</td>
              <td class="text-right">{{ monto(item.TOTAL) }}</td>
              <td v-if="!datos.corte">
                <v-btn v-if="item.indiceAjuste !== undefined" icon x-small title="Quitar ajuste" @click="quitarAjuste(item.indiceAjuste)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </td>
            </tr>
            <tr class="font-weight-bold">
              <td colspan="3">Total a facturar</td>
              <td class="text-right">{{ monto(s.TOTAL_FACTURA) }}</td>
              <td v-if="!datos.corte"></td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>

      <div v-if="datos.revisiones.length">
        <p class="subtitle-2 mb-1">Revisiones</p>
        <v-simple-table dense>
          <thead>
            <tr>
              <th>Revisión</th>
              <th>Estado</th>
              <th>Aprobó</th>
              <th>Anuló</th>
              <th>Motivo</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in datos.revisiones" :key="r.REVISION">
              <td>{{ r.REVISION }}</td>
              <td>{{ r.ESTADO }}</td>
              <td>{{ r.ING_USUARIO_ID }}, {{ r.ING_FECHA }}</td>
              <td><span v-if="r.MOD_USUARIO_ID">{{ r.MOD_USUARIO_ID }}, {{ r.MOD_FECHA }}</span></td>
              <td>{{ r.MOTIVO_ANULA }}</td>
              <td class="text-right">{{ monto(r.TOTAL_FACTURA) }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </div>

    <v-dialog v-model="dialogAjuste" max-width="500px" persistent>
      <v-card>
        <v-card-title>Ajuste manual</v-card-title>
        <v-card-text>
          <v-form ref="formAjuste">
            <v-select
              v-model="ajuste.SCLIENTE_ID"
              :items="datos ? datos.subclientes : []"
              item-text="SCLIENTE_NOMBRE"
              item-value="SCLIENTE_ID"
              label="Destino"
              :rules="requerido"
            ></v-select>
            <v-text-field v-model="ajuste.DESCRIPCION" label="Descripción" maxlength="255" :rules="requerido"></v-text-field>
            <v-text-field v-model="ajuste.VALOR" type="number" step="0.01" label="Valor (negativo para restar)" :rules="valorAjuste"></v-text-field>
            <v-text-field v-model="ajuste.MOTIVO" label="Motivo" maxlength="255" :rules="requerido"></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small @click="dialogAjuste = false">Cancelar</v-btn>
          <v-btn small color="primary" @click="agregarAjuste()">Agregar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogAprobar" max-width="450px" persistent>
      <v-card>
        <v-card-title>Aprobar corte</v-card-title>
        <v-card-text>
          Se congelarán los valores de {{ subclientes.length }} destino(s), por un total de {{ monto(totalFactura) }}
          <span v-if="ajustes.length">, con {{ ajustes.length }} ajuste(s)</span>.
          Los cambios posteriores en la guía o las tarifas no modifican el corte.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small @click="dialogAprobar = false">Cancelar</v-btn>
          <v-btn small color="primary" :loading="guardando" @click="aprobar()">Aprobar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogAnular" max-width="450px" persistent>
      <v-card>
        <v-card-title>Anular corte</v-card-title>
        <v-card-text>
          <p>La revisión quedará anulada, no se borra.</p>
          <v-text-field v-model="motivoAnula" label="Motivo" maxlength="255"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small @click="dialogAnular = false">Cancelar</v-btn>
          <v-btn small color="error" :loading="guardando" :disabled="!motivoAnula.trim()" @click="anular()">Anular</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapMutations } from "vuex";

const ajusteVacio = () => ({ SCLIENTE_ID: null, DESCRIPCION: "", VALOR: "", MOTIVO: "" });

export default {
  name: "FacturacionGuia",
  props: {
    cabeceraId: { type: [Number, String], required: true },
  },
  data: () => ({
    cargando: false,
    guardando: false,
    datos: null,
    mensaje: "",
    ajustes: [],
    ajuste: ajusteVacio(),
    dialogAjuste: false,
    dialogAprobar: false,
    dialogAnular: false,
    motivoAnula: "",
    requerido: [(v) => (!!v && String(v).trim() !== "") || "El campo es requerido"],
    valorAjuste: [(v) => (v !== "" && Number(v) !== 0) || "Indique un valor distinto de 0"],
  }),
  computed: {
    // Con corte se muestran los valores congelados; sin corte, el calculo actual con los ajustes en preparacion.
    subclientes() {
      if (!this.datos) {
        return [];
      }
      if (this.datos.corte) {
        return this.datos.corte.subclientes;
      }
      return this.datos.subclientes.map((s) => {
        const items = [...s.ITEMS];
        let total = s.TOTAL_FACTURA;
        this.ajustes.forEach((a, indiceAjuste) => {
          if (a.SCLIENTE_ID === s.SCLIENTE_ID) {
            const valor = Number(a.VALOR);
            items.push({ DESCRIPCION: a.DESCRIPCION, MOTIVO: a.MOTIVO, CANTIDAD: 1, VALOR_UNI: valor, TOTAL: valor, indiceAjuste });
            total = total === null ? null : total + valor;
          }
        });
        return { ...s, ITEMS: items, TOTAL_FACTURA: total };
      });
    },
    totalFactura() {
      return this.subclientes.reduce((suma, s) => suma + (s.TOTAL_FACTURA || 0), 0);
    },
  },
  watch: {
    cabeceraId: {
      immediate: true,
      handler() {
        this.ajustes = [];
        this.cargar();
      },
    },
  },
  methods: {
    ...mapMutations("master", ["setUrl"]),
    ...mapActions("master", ["requestApi", "alertNotification"]),

    // Un monto sin calcular se muestra como raya, para no confundirlo con 0.
    monto(valor) {
      if (valor === null || valor === undefined) {
        return "—";
      }
      return Number(valor).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    cargar() {
      this.cargando = true;
      this.mensaje = "";
      this.setUrl("api/facturacion-guia");
      this.requestApi({ method: "POST", data: { cabecera_id: this.cabeceraId } })
        .then((res) => {
          this.datos = res.data.success ? res.data : null;
          this.mensaje = res.data.success ? "" : res.data.msg;
        })
        .catch(() => {})
        .then(() => {
          this.cargando = false;
        });
    },

    abrirAjuste() {
      this.ajuste = ajusteVacio();
      this.dialogAjuste = true;
      this.$nextTick(() => {
        if (this.$refs.formAjuste) {
          this.$refs.formAjuste.resetValidation();
        }
      });
    },

    agregarAjuste() {
      if (this.$refs.formAjuste.validate()) {
        this.ajustes.push({ ...this.ajuste });
        this.dialogAjuste = false;
      }
    },

    quitarAjuste(indice) {
      this.ajustes.splice(indice, 1);
    },

    aprobar() {
      this.guardando = true;
      this.setUrl("api/corte-aprobar");
      this.requestApi({ method: "POST", data: { cabecera_id: this.cabeceraId, ajustes: this.ajustes } })
        .then((res) => {
          this.alertNotification({ param: { html: res.data.msg } });
          this.ajustes = [];
          this.cargar();
        })
        .catch(() => {})
        .then(() => {
          this.guardando = false;
          this.dialogAprobar = false;
        });
    },

    abrirAnular() {
      this.motivoAnula = "";
      this.dialogAnular = true;
    },

    anular() {
      this.guardando = true;
      this.setUrl("api/corte-anular");
      this.requestApi({ method: "POST", data: { cabecera_id: this.cabeceraId, motivo: this.motivoAnula } })
        .then((res) => {
          this.alertNotification({ param: { html: res.data.msg } });
          this.dialogAnular = false;
          this.cargar();
        })
        .catch(() => {})
        .then(() => {
          this.guardando = false;
        });
    },
  },
};
</script>
